// Serialize/rebuild FormData (incl. offline photos) for the mutation outbox.
import _ from "lodash";
import { savePhotoLocally } from "./photos";

export const normalizeUrl = (config) => {
  const url = config?.url || "";
  // Strip baseURL if accidentally absolute
  try {
    if (_.startsWith(url, "http")) {
      const u = new URL(url);
      return `${u.pathname}${u.search}`;
    }
  } catch (_e) {
    // ignore
  }
  return url;
};

export const isAuthUrl = (url = "") =>
  _.includes(url, "/auth/login") || _.includes(url, "/auth/refresh") || _.includes(url, "/auth/logout");

// Promote localPath string fields (from offline uploadNow) into file entries for sync upload
const enrichLocalPathFiles = (entries) => {
  const next = [...entries];
  const localPaths = _.filter(entries, (e) => e.kind === "string" && _.endsWith(e.key, "[localPath]") && e.value);
  _.each(localPaths, (lp) => {
    const prefix = lp.key.slice(0, -"[localPath]".length);
    if (_.some(next, (e) => e.kind === "file" && _.startsWith(e.key, prefix))) return;
    const nameEntry = _.find(entries, (e) => e.key === `${prefix}[name]`);
    const typeEntry = _.find(entries, (e) => e.key === `${prefix}[type]`);
    next.push({
      key: `${prefix}[__offline_file]`,
      kind: "file",
      localPath: lp.value,
      name: nameEntry?.value || "photo.jpg",
      type: typeEntry?.value || "image/jpeg",
      attachmentUpload: true,
      signedIdKey: `${prefix}[blob_signed_id]`,
    });
  });
  return next;
};

export const serializeRequestBody = async (data) => {
  if (data == null) return { bodyKind: "empty" };

  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const entries = [];
    for (const [key, value] of data.entries()) {
      if (value instanceof File || (typeof Blob !== "undefined" && value instanceof Blob && value.name != null)) {
        const file = value instanceof File ? value : new File([value], value.name || "file", { type: value.type });
        const saved = await savePhotoLocally(file);
        entries.push({ key, kind: "file", ...saved });
      } else if (typeof Blob !== "undefined" && value instanceof Blob) {
        const file = new File([value], "blob", { type: value.type });
        const saved = await savePhotoLocally(file);
        entries.push({ key, kind: "file", ...saved });
      } else {
        entries.push({ key, kind: "string", value: String(value) });
      }
    }
    return { bodyKind: "formData", entries: enrichLocalPathFiles(entries) };
  }

  if (_.isObject(data) || _.isArray(data)) {
    return { bodyKind: "json", bodyJson: data };
  }

  return { bodyKind: "raw", bodyJson: data };
};

export const rebuildRequestBody = async (serialized, { readPhotoAsFile }) => {
  if (!serialized || serialized.bodyKind === "empty") return undefined;

  if (serialized.bodyKind === "formData") {
    const fd = new FormData();
    for (const entry of serialized.entries || []) {
      if (entry.kind === "file") {
        const file = await readPhotoAsFile(entry);
        fd.append(entry.key, file, entry.name || file.name);
      } else {
        fd.append(entry.key, entry.value);
      }
    }
    return fd;
  }

  if (serialized.bodyKind === "json" || serialized.bodyKind === "raw") {
    return serialized.bodyJson;
  }

  return undefined;
};

export const extractIdFromUrl = (url = "") => {
  const path = url.split("?")[0];
  const match = path.match(/\/(\d+)(?:\.json)?$/);
  return match ? Number(match[1]) : null;
};

export const extractProjectIdFromUrl = (url = "") => {
  const match = (url.split("?")[0] || "").match(/\/project\/(\d+)(?:\/|$)/);
  return match ? Number(match[1]) : null;
};

export const fieldsFromSerializedBody = (serialized = {}) => {
  const fields = {};
  if (serialized.bodyKind === "formData") {
    _.each(serialized.entries, (e) => {
      if (e.kind !== "string") return;
      const m = e.key.match(/^[^\[]+\[([^\]]+)\]$/);
      if (m) fields[m[1]] = e.value;
    });
  }
  if (serialized.bodyKind === "json" && _.isPlainObject(serialized.bodyJson)) {
    const nested = _.find(_.values(serialized.bodyJson), (v) => _.isPlainObject(v) && !_.isArray(v));
    Object.assign(fields, nested || serialized.bodyJson);
  }
  return fields;
};

export const fieldFromSerializedBody = (serialized, name) => fieldsFromSerializedBody(serialized)[name];

export const buildOptimisticEntity = ({ method, url, clientTempId, cachedEntity, serialized }) => {
  const now = new Date().toISOString();
  const fields = fieldsFromSerializedBody(serialized);
  const projectId = extractProjectIdFromUrl(url) || fields.project_id;
  if (_.toLower(method) === "post") {
    return {
      ...fields,
      id: clientTempId,
      project_id: projectId || fields.project_id,
      _pendingSync: true,
      display_name: fields.display_name || fields.description || fields.name || "(pending sync)",
      created_at: now,
      updated_at: now,
    };
  }
  if (cachedEntity) {
    return { ...cachedEntity, ...fields, _pendingSync: true, updated_at: now };
  }
  const id = extractIdFromUrl(url);
  return id != null ? { id, project_id: projectId, ...fields, _pendingSync: true, updated_at: now } : { ...fields, _pendingSync: true, updated_at: now };
};
